import React, { useState, useMemo, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import ActionsModal from 'components/ActionsModal'
import MultiSelect from 'react-multi-select-component'
import DropdownInput from 'components/DropdownInput'
import { selectDisplayedZone, updateDisplayedZone, closeDisplayedZone } from 'store/organizations'
import messages from './messages'
import { selectVerticals, selectAllGroups, fetchGroups } from 'store/global'
import styles from './ZoneModal.scss'

const allowedModes = ['create', 'edit', 'delete', 'view']

function formatZoneName(name) {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_')
    .replace(/\s+/g, '_')
}
    

function toSnakeCase(camelCaseString) {
  return camelCaseString
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase();
}

export default function ZoneModal({ mode, onSubmit }) {
  const zone = useSelector(selectDisplayedZone)
  const verticals = useSelector(selectVerticals)
  const allGroups = useSelector(selectAllGroups)
  const dispatch = useDispatch()

  const [selectedVerticals, setSelectedVerticals] = useState(Object.keys(zone?.verticals || {}))
  const [selectedGroups, setSelectedGroups] = useState([])

  useEffect(() => {
    if (!allGroups || allGroups.length === 0) {
      dispatch(fetchGroups())
    }
  }, [dispatch, allGroups])

  useEffect(() => {
    if (zone?.groups) {
      const groupOptions = zone.groups.map(groupPath => {
        const group = findGroupByPath(allGroups, groupPath)
        return group ? { value: group.id, label: group.path } : null
      }).filter(option => option !== null)
      setSelectedGroups(groupOptions)
    } else {
      setSelectedGroups([])
    }
  }, [zone?.groups, allGroups])

  const findGroupByPath = (groups, targetPath) => {
    for (const group of groups || []) {
      if (group.path === targetPath) {
        return group
      }
      if (group.subGroups && group.subGroups.length > 0) {
        const found = findGroupByPath(group.subGroups, targetPath)
        if (found) return found
      }
    }
    return null
  }

  const flattenGroupsForOptions = (groups, level = 0) => {
    let allGroups = []
    groups.forEach(group => {
      allGroups.push({
        value: group.id,
        label: group.path,
        path: group.path,
      })
      if (group.subGroups && group.subGroups.length > 0) {
        allGroups = allGroups.concat(flattenGroupsForOptions(group.subGroups, level + 1))
      }
    })
    return allGroups
  }

  const groupOptions = useMemo(() => {
    return flattenGroupsForOptions(allGroups || [])
  }, [allGroups])

  const onFieldChange = (key, value) => {
    if (key === 'verticals') {
      setSelectedVerticals(value.map(item => item.value));
    }
    dispatch(updateDisplayedZone({ key, value }))    
  }

  const onGroupsChange = (nextGroupsSelections) => {
    setSelectedGroups(nextGroupsSelections)
  }

  const getVerticalsOptions = () => {
    return verticals.map(vertical => ({
      label: vertical.name,
      value: vertical.id,
    }))
  }

  const getVerticalsValue = () => {
    return selectedVerticals.map(key => ({
      label: verticals.find(v => v.id === key)?.name || key,
      value: key,
    }));
  }

  const onVerticalsChange = (data) => {
    const selectedKeys = data.map(item => item.value);
    setSelectedVerticals(selectedKeys);
  }

  const onSaveModal = () => {
    const updatedVerticals = { ...zone.verticals || {} };

    selectedVerticals.forEach(key => {
      if (!Object.keys(updatedVerticals).includes(key)) {
        updatedVerticals[key] = {
          baseIndex: `${formatZoneName(zone.name)}_${toSnakeCase(key)}_index`,
          activityIndex: `${formatZoneName(zone.name)}_${toSnakeCase(key)}_activity_index`,
        };
      }
    });

    Object.keys(updatedVerticals).forEach(key => {
      if (!selectedVerticals.includes(key)) {
        delete updatedVerticals[key];
      }
    });

    const updatedGroups = selectedGroups.map(groupOption => groupOption.label)

    const zoneData = {
      ...zone,
      verticals: { ...updatedVerticals },
      groups: updatedGroups
    };

    Object.keys(zoneData).forEach(key => {
      if (zoneData[key] === undefined || zoneData[key] === null) {
        delete zoneData[key];
      }
    });

    dispatch(updateDisplayedZone({ key: 'verticals', value: updatedVerticals }));
    dispatch(updateDisplayedZone({ key: 'groups', value: updatedGroups }));
    onSubmit(zoneData);
  }

  const onCloseModal = () => dispatch(closeDisplayedZone())

  const fields = {
    name: {
      key: 'name',
      isRequired: true,
      validator: value => value.length > 0,
      label: <FormattedMessage {...messages.labels.name} />,
    },
    groups: {
      key: 'groups',
      isRequired: false,
      label: <FormattedMessage {...messages.labels.groups} />,
      renderInput: (props = {}) => {
        if (mode === 'view') {
          const groups = zone?.groups || []
          if (groups.length === 0) {
            return (
              <div className={styles.readOnlyField}>
                No groups assigned
              </div>
            )
          }
          
          const shortGroupNames = groups.map(group => {
            const parts = group.split('/')
            return parts[parts.length - 1]
          })
          const displayText = shortGroupNames.join(', ')
          const fullText = groups.join(', ')
          
          return (
            <div 
              className={styles.readOnlyField}
              title={fullText}
            >
              {displayText}
            </div>
          )
        }

        return (
          <div>
            <DropdownInput
              options={groupOptions}
              selections={selectedGroups}
              onChange={onGroupsChange}
              hasSelectAll={false}
              disableSearch={false}
              dropdownHeader="Select groups..."
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              Selected: {selectedGroups.length} groups
            </div>
          </div>
        )
      },
    },
    verticals: {
      key: 'verticals',
      isRequired: false,
      type: 'select',
      label: <FormattedMessage {...messages.labels.verticals} />,
      renderInput: (props = {}) => (
        <MultiSelect
          disableSearch
          hasSelectAll={false}
          options={getVerticalsOptions()}
          value={getVerticalsValue()}
          onChange={onVerticalsChange}
          className={`${styles.multiSelect} ${verticals.length && styles.multiSelectHasValue} ${
            styles[props?.disabled]
          }`}
          {...props}
        />
      ),
    },
  }

  return (
    <ActionsModal
      type='zone'
      mode={mode}
      item={zone}
      primaryKey='name'
      fields={fields}
      onFieldChange={onFieldChange}
      onClose={onCloseModal}
      onSubmit={onSaveModal}
    />
  )
}

ZoneModal.propTypes = {
  mode: PropTypes.oneOf(allowedModes).isRequired,
  onSubmit: PropTypes.func.isRequired,
}
